import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import { clientService } from "../../utils/services/clientService";
import debounce from "lodash.debounce";

const ClientAutocomplete = ({
  selectedClient,
  onClientChange,
  width = 200,
  label = "Select Client",
  errorMessage,
  hideLabel,
  backgroundColor,
  borderColor,
  style,
  inputStyle,
  /** Extra TextField sx, merged last (lets toolbars match their controls). */
  inputSx,
  margin = "dense",
}) => {
  const [allClients, setAllClients] = useState([]);
  const [clientSearchValue, setClientSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [cachedClients, setCachedClients] = useState([]); // Cache for previously loaded clients
  const [lastSearchQuery, setLastSearchQuery] = useState(""); // Track last search query
  const [cachedPage, setCachedPage] = useState(0); // Cache the current page state

  const fetchClientData = useCallback(
    debounce(async (searchQuery, pageNumber, isLoadMore = false) => {
      if (isLoadMore) {
        setLoadMoreLoading(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await clientService.fetch(
          10,
          pageNumber * 10,
          searchQuery,
          null,
          "Active",
        );
        if (pageNumber === 0) {
          setAllClients(response.data);
          // If it's a new search or first load, update cached clients
          if (searchQuery !== lastSearchQuery) {
            setCachedClients(response.data);
            setLastSearchQuery(searchQuery);
            setCachedPage(0); // Reset cached page for new search
          }
        } else {
          const newClients = [...allClients, ...response.data];
          setAllClients(newClients);
          // Update cached clients with new data
          setCachedClients(newClients);
          setCachedPage(pageNumber); // Update cached page
        }
        setTotal(response.total);
      } catch (error) {
        console.error("Fetch failed:", error);
      } finally {
        if (isLoadMore) {
          setLoadMoreLoading(false);
        } else {
          setLoading(false);
        }
      }
    }, 300),
    [allClients, lastSearchQuery],
  );

  // useEffect(() => {
  //   fetchClientData(clientSearchValue, page)
  // }, [clientSearchValue, page])

  const handleClientChange = (event, value) => {
    if (value && value.business_name === "Load More") {
      return;
    }
    if (onClientChange) {
      onClientChange(value);
    }
    setClientSearchValue("");
    // Don't reset page here - keep the cached data
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setClientSearchValue(value);
    fetchClientData(value, 0);
    setPage(0);
    setAllClients([]);
    // Reset cached clients when starting a new search
    setCachedClients([]);
    setCachedPage(0);
    setLastSearchQuery(value);
  };

  const handleLoadMoreClick = () => {
    if (allClients.length < total && !loading && !loadMoreLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      setCachedPage(nextPage); // Update cached page
      fetchClientData(clientSearchValue, nextPage, true);
    }
  };

  // Add "Load More" option every 10 clients if more data is available
  const optionsWithLoadMore = [...allClients];
  if (allClients.length < total && allClients.length % 10 === 0) {
    optionsWithLoadMore.push({ business_name: "Load More" });
  }

  return (
    <Autocomplete
      size="small"
      id="client-select"
      options={optionsWithLoadMore}
      getOptionLabel={(option) => option.business_name || option.name || ""}
      value={selectedClient}
      onChange={handleClientChange}
      onOpen={() => {
        // When opening, restore cached clients if available and search query matches
        if (cachedClients.length > 0 && clientSearchValue === lastSearchQuery) {
          setAllClients(cachedClients);
          setPage(cachedPage); // Restore the cached page state
        } else if (cachedClients.length === 0) {
          // Only fetch if no cached data
          fetchClientData("", 0, false);
        }
      }}
      onClose={() => {
        // Don't reset anything on close - keep the cached data
      }}
      renderOption={(props, option) =>
        option.business_name === "Load More" ? (
          <li
            {...props}
            onClick={handleLoadMoreClick}
            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <Typography
              color="primary"
              variant="body2"
              style={{ marginRight: 8 }}
            >
              Load More...
            </Typography>
            {loadMoreLoading && (
              <CircularProgress color="inherit" size={16} />
            )}{" "}
          </li>
        ) : (
          <li {...props}>{option.business_name}</li>
        )
      }
      renderInput={(params) => (
        <TextField
          fullWidth
          style={{ ...inputStyle }}
          onClick={() => {
            // Only fetch if no cached data exists
            if (cachedClients.length === 0) {
              fetchClientData("", 0, false);
            }
          }}
          sx={{
            backgroundColor: backgroundColor,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: borderColor || "var(--border-color)",
              },
              "&:hover fieldset": {
                borderColor: borderColor || "var(--border-color)",
              },
              "&.Mui-focused fieldset": {
                borderColor: borderColor || "var(--border-color)",
              },
            },
            "& .MuiAutocomplete-popupIndicator": {
              color: borderColor,
            },
            "& .MuiAutocomplete-clearIndicator": {
              color: borderColor,
            },
            ...inputSx,
          }}
          {...params}
          margin={margin}
          label={hideLabel ? "" : label}
          placeholder="Select Client"
          variant="outlined"
          onChange={handleSearchChange}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={!!errorMessage}
          helperText={errorMessage}
        />
      )}
      style={{ minWidth: width, marginRight: 10, ...style }}
    />
  );
};

export default ClientAutocomplete;
